import React from 'react';
import { makeStyles, TextField } from '@material-ui/core';

import Dialog from '@material-ui/core/Dialog';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Slide from '@material-ui/core/Slide';
import Hidden from '@material-ui/core/Hidden';

import Joi from '@hapi/joi';
import { useForm, Controller } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { ErrorMessage } from '@hookform/error-message';
import formErrorMessage from '@/configs/formErrorMessage';
import { useProgressContext } from '@/contexts/useProgressContext';
import { useAuthState } from '@/plugins/Auth';

import LabelTextField from '@/components/LabelTextField';
import Alert from '@/components/Alert';
import config from '@/configs/config';
import preOrderAPI from '@/api/preOrder';

const SUCCESS_ALERT = 'SUCCESS_ALERT';
const FAILED_ALERT = 'FAILED_ALERT';

const useStyles = makeStyles((theme) => ({
  dialogScrollPaper: {
    alignItems: 'flex-end',
    [theme.breakpoints.up('sm')]: {
      maxWidth: '80vw',
      margin: '0 auto',
      width: 500,
      alignItems: 'center',
    },
  },
  dialogPaper: {
    height: 'auto',
    backgroundColor: theme.palette.grey[300],
    paddingBottom: 'env(safe-area-inset-bottom, 0)',
  },
  actionButton: {
    color: 'white',
    backgroundColor: '#54AD7A !important',
  },
  mobileInput: {
    width: '100%',
    height: '100%',
    '& input': {
      backgroundColor: 'rgb(242, 242, 242)',
      border: 'rgb(227, 227, 227)',
    },
  },
}));

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />); // eslint-disable-line

const schema = Joi.object({
  code: Joi.string()
    .required(),
});

const MagicCodeInput = ({
  checkoutData,
  handleClose: triggerClose,
  handleNewMagicCodeApplied,
  magicCodeData = null,
}) => {
  const classes = useStyles();
  const { token } = useAuthState();
  const {
    start: startProgress,
    end: endProgress,
  } = useProgressContext();
  const [activeAlert, setActiveAlert] = React.useState(null);
  const {
    errors,
    control,
    formState,
    getValues,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      code: magicCodeData?.magicCode || '',
    },
    resolver: joiResolver(schema, {
      abortEarly: false,
      messages: formErrorMessage,
    }),
  });

  const handleSubmit = React.useCallback(() => {
    const code = getValues('code');
    setActiveAlert(null);
    startProgress();
    preOrderAPI.checkMagicCode({
      body: {
        magicCode: code,
        token,
        checkoutData,
      },
    }).then(({
      status,
      title,
      discountPrice,
    }) => {
      if (status !== 'OK') {
        setActiveAlert(FAILED_ALERT);
        return;
      }
      setActiveAlert(SUCCESS_ALERT);
      handleNewMagicCodeApplied({
        title,
        discountPrice,
        magicCode: code,
      });
    }).finally(() => {
      endProgress();
    });
  }, [
    token,
    startProgress,
    endProgress,
    checkoutData,
    handleNewMagicCodeApplied,
    getValues,
  ]);

  return (
    <>
      <Hidden only="xs">
        <Dialog
          classes={{
            scrollPaper: classes.dialogScrollPaper,
            paper: classes.dialogPaper,
          }}
          fullScreen
          open
          onClose={triggerClose}
          TransitionComponent={Transition}
        >
          <form>
            <Box px={3} py={2}>
              <Box px={1} pt={2} pb={4}>
                <Typography>
                  請在下方輸入折扣碼
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="code"
                    render={({ ref, ...props }) => (
                      <LabelTextField
                        label="折扣碼"
                        placeholder={`ex: ${config.BrandNameEN.toUpperCase()}`}
                        inputRef={ref}
                        {...props} // eslint-disable-line
                      />
                    )}
                  />
                  <Box mt={3} color="error.dark" fontSize="body2.fontSize">
                    <ErrorMessage errors={errors} name="code" />
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Box width={1}>
              <Button
                onClick={handleSubmit}
                variant="contained"
                fullWidth
                size="large"
                color="secondary"
                disabled={!formState.isValid}
              >
                <Box py={1} fontSize="body1.fontSize">
                  套用
                </Box>
              </Button>
            </Box>
          </form>
        </Dialog>
        <Alert
          isOpen={activeAlert === SUCCESS_ALERT}
          title="成功"
          content="折扣碼使用成功。"
          buttons={(
            <Button fullWidth onClick={triggerClose}>
              <Box fontWeight="fontWeightBold" color="support.main">
                關閉
              </Box>
            </Button>
            )}
        />
        <Alert
          isOpen={activeAlert === FAILED_ALERT}
          title="折扣碼無法使用"
          content="折扣碼無效。可能是輸入錯誤或超過使用期限，請重新輸入。"
          buttons={(
            <Button fullWidth onClick={() => setActiveAlert(null)}>
              <Box fontWeight="fontWeightBold" color="support.main">
                關閉
              </Box>
            </Button>
          )}
        />
      </Hidden>
      <Hidden only="sm">
        <form>
          <Box
            mx="auto"
            borderTop="1px solid rgb(222, 222, 222)"
            p={1}
            display="flex"
          >
            <Box mx={1} flex="1" position="relative">
              <Box>
                <Controller
                  control={control}
                  name="code"
                  render={({ ref, ...props }) => (
                    <TextField
                      className={classes.mobileInput}
                      disabled={!!magicCodeData}
                      placeholder="輸入折扣碼"
                      inputRef={ref}
                      {...props}
                    />
                  )}
                />
              </Box>
              {
              magicCodeData && (
                <Box
                  height="100%"
                  position="absolute"
                  right={5}
                  top={0}
                >
                  <Button
                    style={{
                      color: 'rgb(83, 83, 83)',
                      textDecoration: 'underline',
                    }}
                    onClick={() => {
                      handleNewMagicCodeApplied(null);
                      setActiveAlert(null);
                    }}
                  >
                    移除折扣碼
                  </Button>
                </Box>
              )
            }
            </Box>
            <Button
              onClick={handleSubmit}
              className={classes.actionButton}
              disabled={!formState.isValid || !!magicCodeData}
            >
              套用
            </Button>
          </Box>
        </form>
        {
          activeAlert === SUCCESS_ALERT && (
            <Box color="rgb(176, 43, 43)" p={1} fontSize="14px">
              折扣碼使用成功。
            </Box>
          )
        }
        {
          activeAlert === FAILED_ALERT && (
            <Box color="rgb(176, 43, 43)" p={1} fontSize="14px">
              折扣碼無效。可能是輸入錯誤或超過使用期限，請重新輸入。
            </Box>
          )
        }
      </Hidden>
    </>
  );
};

export default MagicCodeInput;
